import React, { useRef, useEffect, useState } from 'react';
import { DndContext, useDraggable, type DragEndEvent, type DragMoveEvent, MouseSensor, useSensor, useSensors } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { useEditorStore, type CanvasElement, type ElementType } from '../../stores/editorStore';
import { uploadImage } from '../../api/client';
import { positioning } from '../../services/elementPositioning';

const elementTypes: { type: ElementType; label: string; icon: string }[] = [
  { type: 'title-block', label: 'Titre', icon: 'H' },
  { type: 'text', label: 'Texte', icon: 'T' },
  { type: 'image', label: 'Image', icon: '🖼️' },
  { type: 'youtube', label: 'YouTube', icon: '▶️' },
];

type CanvasProps = {
  hideControls?: boolean;
  scale?: number;
};

type ResizeHandle = 'nw' | 'ne' | 'sw' | 'se' | 'n' | 's' | 'e' | 'w';

const DraggableElement: React.FC<{
  element: CanvasElement;
  isSelected: boolean;
  isConnecting: boolean;
  isDraggingAnyConnection: boolean;
  isDraggingAnyElement: boolean;
  onClick: () => void;
  onConnectDragStart?: (e: React.MouseEvent) => void;
  hideControls: boolean;
  onResize?: (handle: ResizeHandle, mouseX: number, mouseY: number, initialElement: CanvasElement) => void;
  onUpdate: (changes: Partial<CanvasElement>) => void;
  scale: number;
}> = ({ element, isSelected, isConnecting, isDraggingAnyConnection, isDraggingAnyElement, onClick, onConnectDragStart, hideControls, onResize, onUpdate, scale }) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ 
    id: element.id,
  });
  
  // Adjust transform for scale - screen pixels need to be converted to canvas pixels
  const scaledTransform = transform ? {
    ...transform,
    x: transform.x / scale,
    y: transform.y / scale,
  } : null;
  const [resizing, setResizing] = useState<{ handle: ResizeHandle; startX: number; startY: number; initialElement: CanvasElement } | null>(null);
  const [isHovered, setIsHovered] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(element.content || '');
  const [isDragOver, setIsDragOver] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Update editValue when element content changes externally
  useEffect(() => {
    if (!isEditing) {
      setEditValue(element.content || '');
    }
  }, [element.content, isEditing]);

  // Auto-size text and title-block elements based on content
  const prevContentRef = useRef(element.content);
  const prevFontSizeRef = useRef(element.fontSize);
  const onUpdateRef = useRef(onUpdate);
  onUpdateRef.current = onUpdate;
  
  useEffect(() => {
    if (element.type !== 'text' && element.type !== 'title-block') return;
    if (!element.content) return;
    
    // Only recalculate if content or fontSize actually changed
    if (prevContentRef.current === element.content && prevFontSizeRef.current === element.fontSize) {
      return;
    }
    prevContentRef.current = element.content;
    prevFontSizeRef.current = element.fontSize;
    
    // Create a temporary canvas to measure text
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    const fontSize = element.fontSize || (element.type === 'title-block' ? 48 : 32);
    ctx.font = `${fontSize}px sans-serif`;
    
    const textWidth = ctx.measureText(element.content).width;
    const padding = 40;
    const newWidth = textWidth + padding;
    const newHeight = Math.max(50, fontSize * 1.5 + 20);
    
    onUpdateRef.current({ width: Math.round(newWidth), height: Math.round(newHeight) });
  }, [element.content, element.fontSize, element.type]);

  const handleDoubleClick = (e: React.MouseEvent) => {
    if (hideControls) return;
    if (element.type === 'text' || element.type === 'title-block') {
      e.stopPropagation();
      setIsEditing(true);
      setEditValue(element.content || '');
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  };

  const handleTextSave = () => {
    onUpdate({ content: editValue });
    setIsEditing(false);
  };

  const handleTextKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleTextSave();
    } else if (e.key === 'Escape') {
      setEditValue(element.content || '');
      setIsEditing(false);
    }
  };

  const handleFileDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) {
      await handleImageUpload(file);
    }
  };

  const handleImageUpload = async (file: File) => {
    setIsUploading(true);
    try {
      const result = await uploadImage(file);
      onUpdate({ src: result.url });
    } catch (error) {
      console.error('Upload failed:', error);
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleImageUpload(file);
  };

  const renderContent = () => {
    switch (element.type) {
      case 'text':
      case 'title-block':
        if (isEditing) {
          return (
            <input
              ref={inputRef}
              type="text"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onBlur={handleTextSave}
              onKeyDown={handleTextKeyDown}
              className="w-full h-full bg-transparent text-center outline-none border-none p-2"
              style={{ fontSize: element.fontSize || 16, color: element.color || '#ffffff' }}
            />
          );
        }
        return (
          <div 
            className="w-full h-full flex items-center justify-center cursor-text overflow-visible"
            style={{ fontSize: element.fontSize || 16, color: element.color || '#ffffff', lineHeight: 1.3 }}
            onDoubleClick={handleDoubleClick}
          >
            {element.content || (hideControls ? '' : 'Double-cliquez pour éditer')}
          </div>
        );
      case 'image':
        if (element.src) {
          return (
            <img 
              src={element.src} 
              alt="" 
              className="w-full h-full object-cover rounded"
              draggable={false}
            />
          );
        }
        // Empty image - show drop zone
        return (
          <div
            className={`w-full h-full flex flex-col items-center justify-center text-white/50 text-sm gap-2 ${
              isDragOver ? 'bg-blue-500/20 border-blue-400' : ''
            }`}
            onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
            onDragLeave={() => setIsDragOver(false)}
            onDrop={handleFileDrop}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileInputChange}
              className="hidden"
            />
            {isUploading ? (
              <span>Upload en cours...</span>
            ) : (
              <>
                <span>🖼️ Glissez une image ici</span>
                <button
                  onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
                  className="px-2 py-1 bg-gray-600 hover:bg-gray-500 rounded text-xs"
                >
                  ou cliquez pour choisir
                </button>
              </>
            )}
          </div>
        );
      case 'youtube':
        if (element.videoId) {
          // In presentation mode, show the actual video
          if (hideControls) {
            return (
              <iframe
                width="100%"
                height="100%"
                src={`https://www.youtube.com/embed/${element.videoId}?autoplay=${element.autoplay ? 1 : 0}&mute=${element.muted ? 1 : 0}${element.startTime ? `&start=${element.startTime}` : ''}`}
                title="YouTube video"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="rounded"
              />
            );
          }
          // In editor mode, show a clickable link
          const videoUrl = `https://youtu.be/${element.videoId}${element.startTime ? `?t=${element.startTime}` : ''}`;
          return (
            <div className="w-full h-full flex flex-col items-center justify-center bg-black/50 rounded p-4">
              <span className="text-4xl mb-2">▶️</span>
              <a
                href={videoUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="text-blue-400 hover:text-blue-300 underline text-sm truncate max-w-full"
              >
                {videoUrl}
              </a>
            </div>
          );
        }
        // Empty YouTube - show placeholder
        return (
          <div className="w-full h-full flex flex-col items-center justify-center text-white/50 text-sm gap-2 p-4 bg-black/50 rounded">
            <span className="text-4xl">▶️</span>
            <span>YouTube</span>
          </div>
        );
      default:
        return null;
    }
  };

  const getElementClasses = () => {
    const baseClasses = 'border-2 rounded-lg';
    
    // Show selection styling only when not hiding controls
    if (isSelected && !hideControls) {
      return `${baseClasses} bg-gray-800/80 border-accent shadow-lg shadow-accent/50`;
    }
    
    return `${baseClasses} bg-gray-800/80 border-gray-500`;
  };

  const renderResizeHandles = () => {
    if (hideControls || !isSelected || isDragging || resizing) return null;

    const handles: { position: string; cursor: string; handle: ResizeHandle }[] = [
      { position: '-top-1 -left-1', cursor: 'nw-resize', handle: 'nw' },
      { position: '-top-1 -right-1', cursor: 'ne-resize', handle: 'ne' },
      { position: '-bottom-1 -left-1', cursor: 'sw-resize', handle: 'sw' },
      { position: '-bottom-1 -right-1', cursor: 'se-resize', handle: 'se' },
      { position: '-top-1 left-1/2 -translate-x-1/2', cursor: 'n-resize', handle: 'n' },
      { position: '-bottom-1 left-1/2 -translate-x-1/2', cursor: 's-resize', handle: 's' },
      { position: '-left-1 top-1/2 -translate-y-1/2', cursor: 'w-resize', handle: 'w' },
      { position: '-right-1 top-1/2 -translate-y-1/2', cursor: 'e-resize', handle: 'e' },
    ];

    return handles.map(({ position, cursor, handle }) => (
      <div
        key={handle}
        className={`absolute ${position} w-3 h-3 bg-white border-2 border-accent rounded-full z-20 hover:scale-125`}
        style={{ cursor }}
        onMouseDown={(e) => {
          e.stopPropagation();
          e.preventDefault();
          setResizing({ handle, startX: e.clientX, startY: e.clientY, initialElement: element });
        }}
      />
    ));
  };

  React.useEffect(() => {
    if (!resizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      onResize?.(resizing.handle, e.clientX, e.clientY, resizing.initialElement);
    };

    const handleMouseUp = () => {
      setResizing(null);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [resizing, onResize]);

  return (
    <div
      className={`absolute ${hideControls && element.type !== 'youtube' ? 'pointer-events-none' : 'pointer-events-auto'}`}
      style={{
        left: element.x,
        top: element.y,
        width: element.width,
        height: element.height,
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div
        ref={setNodeRef}
        style={{
          transform: CSS.Translate.toString(scaledTransform),
          width: '100%',
          height: '100%',
          overflow: 'visible',
        }}
        {...(hideControls ? {} : listeners)}
        {...attributes}
        onClick={onClick}
        className={`
          ${getElementClasses()}
          ${!hideControls && isConnecting && isSelected ? 'animate-pulse border-yellow-400' : ''}
          ${isDragging ? 'opacity-50' : 'opacity-100'}
          ${hideControls ? 'cursor-default' : resizing ? 'cursor-auto' : 'cursor-move'}
        `}
      >
        {renderContent()}
        
        {renderResizeHandles()}
      </div>
      
      {!hideControls && !isDraggingAnyConnection && !isDraggingAnyElement && (
        <div
          className={`
            absolute -right-10 top-1/2 -translate-y-1/2 w-8 h-8 
            bg-accent rounded-full border-2 border-white 
            flex items-center justify-center 
            cursor-crosshair z-30
            hover:scale-110 hover:bg-accent/90
            ${isSelected || isHovered ? 'opacity-100' : 'opacity-0'}
          `}
          title="Drag to create connection"
          onMouseDown={(e) => {
            e.stopPropagation();
            onConnectDragStart?.(e);
          }}
        >
          <svg 
            width="16" 
            height="16" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2.5"
            className="text-white"
          >
            <path d="M7 17L17 7M17 7H7M17 7V17" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
      )}
    </div>
  );
};

export const Canvas: React.FC<CanvasProps> = ({ hideControls = false, scale = 1 }) => {
  const { 
    currentEpisode,
    currentSectionIndex,
    selectedElementId, 
    selectElement, 
    moveElement,
    updateElement,
    removeElement,
    addElement,
    createConnection,
    removeConnection,
  } = useEditorStore();
  
  const canvasRef = useRef<HTMLDivElement>(null);
  const scaleRef = useRef(scale);
  scaleRef.current = scale;
  const [canvasBounds, setCanvasBounds] = useState({ width: 0, height: 0 });
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  
  // Zoom and pan state
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const zoomRef = useRef(zoom);
  const panRef = useRef(pan);
  zoomRef.current = zoom;
  panRef.current = pan;
  
  // Total scale combines external scale and internal zoom
  const totalScale = scale * zoom;
  const totalScaleRef = useRef(totalScale);
  totalScaleRef.current = totalScale;
  
  // Connection drag state
  const [isDraggingConnection, setIsDraggingConnection] = useState(false);
  const [connectionFromId, setConnectionFromId] = useState<string | null>(null);
  const connectionFromRef = useRef<string | null>(null);

  // Track active drag for real-time connection updates
  const [activeDrag, setActiveDrag] = useState<{ id: string; delta: { x: number; y: number } } | null>(null);

  // Void drop picker state - when connection is dropped on empty space
  const [voidDropPicker, setVoidDropPicker] = useState<{ x: number; y: number; fromId: string } | null>(null);
  const voidDropJustOpenedRef = useRef(false);

  const currentSection = currentEpisode?.sections[currentSectionIndex];
  const elements = currentSection?.elements || [];
  const connections = currentSection?.connections || [];
  
  // Helper: convert screen coordinates to canvas coordinates
  const screenToCanvas = (screenX: number, screenY: number) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return { x: 0, y: 0 };
    // 1. Get position relative to canvas container
    // 2. Divide by external scale to get local coords
    // 3. Subtract pan offset
    // 4. Divide by zoom to get canvas coords
    const localX = (screenX - rect.left) / scale;
    const localY = (screenY - rect.top) / scale;
    return {
      x: (localX - pan.x) / zoom,
      y: (localY - pan.y) / zoom,
    };
  };
  
  // Ref version for event handlers
  const screenToCanvasRef = (screenX: number, screenY: number) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return { x: 0, y: 0 };
    const localX = (screenX - rect.left) / scaleRef.current;
    const localY = (screenY - rect.top) / scaleRef.current;
    return {
      x: (localX - panRef.current.x) / zoomRef.current,
      y: (localY - panRef.current.y) / zoomRef.current,
    };
  };

  useEffect(() => {
    const updateBounds = () => {
      if (canvasRef.current) {
        const rect = canvasRef.current.getBoundingClientRect();
        // Convert visual size back to canvas coordinates by dividing by scale
        // Note: bounds represent the visible area in canvas coords, not affected by zoom
        setCanvasBounds({ 
          width: rect.width / scale, 
          height: rect.height / scale 
        });
      }
    };
    updateBounds();
    window.addEventListener('resize', updateBounds);
    return () => window.removeEventListener('resize', updateBounds);
  }, [scale]);
  
  // Wheel handler for zoom
  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;
    
    // Get mouse position in local coords (after external scale, before pan/zoom)
    const localX = (e.clientX - rect.left) / scale;
    const localY = (e.clientY - rect.top) / scale;
    
    // Calculate canvas point under cursor before zoom
    const canvasX = (localX - pan.x) / zoom;
    const canvasY = (localY - pan.y) / zoom;
    
    // Calculate new zoom (with limits)
    const zoomFactor = e.deltaY > 0 ? 0.9 : 1.1;
    const newZoom = Math.max(0.25, Math.min(4, zoom * zoomFactor));
    
    // Calculate new pan to keep the same canvas point under cursor
    const newPanX = localX - canvasX * newZoom;
    const newPanY = localY - canvasY * newZoom;
    
    setZoom(newZoom);
    setPan({ x: newPanX, y: newPanY });
  };
  
  // Track if we actually moved during pan (to distinguish click from drag)
  const didPanRef = useRef(false);
  
  // Pan start handler (left click on background)
  const handleMouseDown = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    const isBackground = target === canvasRef.current || target.classList.contains('canvas-background');
    
    if (e.button === 0 && isBackground) {
      // Left click on background - start panning
      e.preventDefault();
      setIsPanning(true);
      didPanRef.current = false;
      setPanStart({ x: e.clientX - pan.x * scale, y: e.clientY - pan.y * scale });
    }
  };
  
  const handleMouseUp = () => {
    if (isPanning) {
      setIsPanning(false);
    }
  };
  
  // Reset zoom/pan with double-click on background
  const handleBackgroundDoubleClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target === canvasRef.current || target.classList.contains('canvas-background')) {
      setZoom(1);
      setPan({ x: 0, y: 0 });
    }
  };
  
  // File drop state and handlers
  const [isFileDragOver, setIsFileDragOver] = useState(false);
  
  const handleFileDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.types.includes('Files')) {
      setIsFileDragOver(true);
    }
  };
  
  const handleFileDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsFileDragOver(false);
  };
  
  const handleFileDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsFileDragOver(false);
    
    const file = e.dataTransfer.files[0];
    if (!file || !file.type.startsWith('image/')) return;
    
    // Get drop position in canvas coordinates
    const dropPos = screenToCanvas(e.clientX, e.clientY);
    
    // Default image element size
    const width = 400;
    const height = 300;
    
    // Center the element on the drop position
    const x = Math.max(0, dropPos.x - width / 2);
    const y = Math.max(0, dropPos.y - height / 2);
    
    // Create the image element first (without src)
    addElement('image', x, y);
    
    // Capture the element ID immediately (before any async operations)
    const newElementId = useEditorStore.getState().selectedElementId;
    if (!newElementId) return;
    
    // Upload the image
    try {
      const result = await uploadImage(file);
      // Update the element with the uploaded image URL
      updateElement(newElementId, { src: result.url, width, height });
    } catch (error) {
      console.error('Failed to upload dropped image:', error);
      // Remove the element if upload failed
      removeElement(newElementId);
    }
  };

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: { distance: 10 },
    })
  );

  const handleDragMove = (event: DragMoveEvent) => {
    // Adjust delta for total scale (external scale * zoom)
    const adjustedDelta = {
      x: event.delta.x / totalScale,
      y: event.delta.y / totalScale,
    };
    setActiveDrag({
      id: String(event.active.id),
      delta: adjustedDelta,
    });
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, delta } = event;
    const element = elements.find((el) => el.id === String(active.id));
    
    setActiveDrag(null);
    
    if (!element) return;

    // Adjust delta for total scale (external scale * zoom)
    const adjustedDelta = {
      x: delta.x / totalScale,
      y: delta.y / totalScale,
    };

    moveElement(String(active.id), element.x + adjustedDelta.x, element.y + adjustedDelta.y);
  };

  const handleCanvasClick = (e: React.MouseEvent) => {
    // Skip if we just finished panning (not a real click)
    if (didPanRef.current) {
      didPanRef.current = false;
      return;
    }
    
    // Only handle clicks directly on the canvas or grid background, not on elements
    const target = e.target as HTMLElement;
    const isCanvasOrBackground = target === canvasRef.current || target.classList.contains('canvas-background');
    
    if (isCanvasOrBackground) {
      if (isDraggingConnection) {
        setIsDraggingConnection(false);
        setConnectionFromId(null);
      } else {
        selectElement(null);
      }
      // Close void drop picker when clicking elsewhere (but not if just opened)
      if (voidDropPicker && !voidDropJustOpenedRef.current) {
        setVoidDropPicker(null);
      }
    }
  };

  const handleElementClick = (id: string) => {
    selectElement(id);
  };

  const handleResize = (elementId: string, handle: ResizeHandle, mouseX: number, mouseY: number, initialElement: CanvasElement) => {
    const minSize = 50;
    
    // Convert screen coords to canvas coords (accounting for scale, pan, and zoom)
    const canvasCoords = screenToCanvas(mouseX, mouseY);
    const canvasMouseX = canvasCoords.x;
    const canvasMouseY = canvasCoords.y;

    let newX = initialElement.x;
    let newY = initialElement.y;
    let newWidth = initialElement.width;
    let newHeight = initialElement.height;

    const right = initialElement.x + initialElement.width;
    const bottom = initialElement.y + initialElement.height;

    switch (handle) {
      case 'se':
        newWidth = Math.max(minSize, canvasMouseX - initialElement.x);
        newHeight = Math.max(minSize, canvasMouseY - initialElement.y);
        break;
      case 'sw':
        newWidth = Math.max(minSize, right - canvasMouseX);
        newHeight = Math.max(minSize, canvasMouseY - initialElement.y);
        newX = Math.min(canvasMouseX, right - minSize);
        break;
      case 'ne':
        newWidth = Math.max(minSize, canvasMouseX - initialElement.x);
        newHeight = Math.max(minSize, bottom - canvasMouseY);
        newY = Math.min(canvasMouseY, bottom - minSize);
        break;
      case 'nw':
        newWidth = Math.max(minSize, right - canvasMouseX);
        newHeight = Math.max(minSize, bottom - canvasMouseY);
        newX = Math.min(canvasMouseX, right - minSize);
        newY = Math.min(canvasMouseY, bottom - minSize);
        break;
      case 'e':
        newWidth = Math.max(minSize, canvasMouseX - initialElement.x);
        break;
      case 'w':
        newWidth = Math.max(minSize, right - canvasMouseX);
        newX = Math.min(canvasMouseX, right - minSize);
        break;
      case 's':
        newHeight = Math.max(minSize, canvasMouseY - initialElement.y);
        break;
      case 'n':
        newHeight = Math.max(minSize, bottom - canvasMouseY);
        newY = Math.min(canvasMouseY, bottom - minSize);
        break;
    }

    // Enforce 16:9 aspect ratio for YouTube elements
    if (initialElement.type === 'youtube') {
      const aspectRatio = 16 / 9;
      // Determine which dimension to constrain based on resize handle
      if (handle === 'e' || handle === 'w') {
        newHeight = newWidth / aspectRatio;
      } else if (handle === 'n' || handle === 's') {
        newWidth = newHeight * aspectRatio;
      } else {
        // Corner resize - use width as primary
        newHeight = newWidth / aspectRatio;
      }
    }

    updateElement(elementId, { 
      x: newX, 
      y: newY, 
      width: newWidth, 
      height: newHeight 
    });
  };

  // Helper function to get the closest edge point of an element
  const getEdgePoint = (el: CanvasElement, target: { x: number; y: number }) => {
    const centerX = el.x + el.width / 2;
    const centerY = el.y + el.height / 2;
    
    // Calculate distances to each edge center
    const edges = [
      { x: centerX, y: el.y, side: 'top' },
      { x: el.x + el.width, y: centerY, side: 'right' },
      { x: centerX, y: el.y + el.height, side: 'bottom' },
      { x: el.x, y: centerY, side: 'left' },
    ];
    
    // Find closest edge to target point
    let closest = edges[0];
    let minDist = Infinity;
    
    edges.forEach(edge => {
      const dist = Math.sqrt((edge.x - target.x) ** 2 + (edge.y - target.y) ** 2);
      if (dist < minDist) {
        minDist = dist;
        closest = edge;
      }
    });
    
    return { x: closest.x, y: closest.y };
  };
  
  // Calculate curved path between two points
  const calculateCurvedPath = (from: { x: number; y: number }, to: { x: number; y: number }) => {
    const midX = (from.x + to.x) / 2;
    return `M${from.x},${from.y} C${midX},${from.y} ${midX},${to.y} ${to.x},${to.y}`;
  };

  const handleConnectDragStart = (elementId: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingConnection(true);
    setConnectionFromId(elementId);
    connectionFromRef.current = elementId;
    
    const handleMouseMove = (moveEvent: MouseEvent) => {
      // Convert screen coords to canvas coords (using refs for current values)
      const canvasCoords = screenToCanvasRef(moveEvent.clientX, moveEvent.clientY);
      setMousePos(canvasCoords);
    };
    
    const handleMouseUp = (upEvent: MouseEvent) => {
      setIsDraggingConnection(false);
      
      // Check if dropped on another element
      const fromId = connectionFromRef.current;
      if (fromId) {
        // Convert screen coords to canvas coords
        const dropCoords = screenToCanvasRef(upEvent.clientX, upEvent.clientY);
        
        // Find element under mouse (excluding the source element)
        const targetElement = elements.find(el => {
          return dropCoords.x >= el.x && dropCoords.x <= el.x + el.width &&
                 dropCoords.y >= el.y && dropCoords.y <= el.y + el.height &&
                 el.id !== fromId;
        });
        
        if (targetElement) {
          createConnection(targetElement.id, 'curve', fromId);
        } else {
          // Dropped on void - show element picker
          setVoidDropPicker({ x: dropCoords.x, y: dropCoords.y, fromId });
          // Prevent the subsequent click event from closing the picker
          voidDropJustOpenedRef.current = true;
          setTimeout(() => { voidDropJustOpenedRef.current = false; }, 100);
        }
      }
      
      setConnectionFromId(null);
      connectionFromRef.current = null;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleMouseMoveCanvas = (e: React.MouseEvent) => {
    // Handle panning
    if (isPanning) {
      const newPanX = (e.clientX - panStart.x) / scale;
      const newPanY = (e.clientY - panStart.y) / scale;
      // Mark that we actually moved (not just a click)
      if (Math.abs(newPanX - pan.x) > 2 || Math.abs(newPanY - pan.y) > 2) {
        didPanRef.current = true;
      }
      setPan({ x: newPanX, y: newPanY });
      return;
    }
    
    if (!isDraggingConnection) {
      const canvasCoords = screenToCanvas(e.clientX, e.clientY);
      setMousePos(canvasCoords);
    }
  };

  // Handle creating element from void drop picker
  const handleVoidDropCreate = (type: ElementType) => {
    if (!voidDropPicker) return;
    
    const fromId = voidDropPicker.fromId;
    
    // Default sizes matching addElement defaults
    const defaultSizes: Record<ElementType, { width: number; height: number }> = {
      text: { width: 200, height: 60 },
      'title-block': { width: 150, height: 80 },
      youtube: { width: 560, height: 315 },
      image: { width: 400, height: 300 },
    };
    
    const size = defaultSizes[type];
    const topLeft = positioning.positionRightOf(voidDropPicker, size);
    
    const newElementId = addElement(type, topLeft.x, topLeft.y);
    
    // Create connection from source to newly created element
    if (newElementId && fromId) {
      createConnection(newElementId, 'curve', fromId);
    }
    
    setVoidDropPicker(null);
  };

  // Handle adding element from empty canvas
  const handleEmptyCanvasAdd = (type: ElementType) => {
    // Add at center of canvas
    const centerX = canvasBounds.width / 2 - 200;
    const centerY = canvasBounds.height / 2 - 50;
    addElement(type, Math.max(50, centerX), Math.max(50, centerY));
  };

  return (
    <DndContext sensors={sensors} onDragMove={handleDragMove} onDragEnd={handleDragEnd}>
      <div className="relative w-full h-full">
        <div 
          ref={canvasRef}
          className={`absolute inset-0 overflow-hidden bg-gray-900/30 ${isFileDragOver ? 'ring-4 ring-inset ring-accent' : ''}`}
          style={{ cursor: isPanning ? 'grabbing' : 'default' }}
          onClick={handleCanvasClick}
          onMouseMove={handleMouseMoveCanvas}
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onWheel={handleWheel}
          onDoubleClick={handleBackgroundDoubleClick}
          onDragOver={handleFileDragOver}
          onDragLeave={handleFileDragLeave}
          onDrop={handleFileDrop}
        >
        {/* Grid background - dots pattern, fills viewport but moves with content */}
        <div 
          className="canvas-background absolute inset-0 opacity-20"
          style={{
            backgroundImage: `radial-gradient(circle, #ffffff ${1.5 * zoom}px, transparent ${1.5 * zoom}px)`,
            backgroundSize: `${48 * zoom}px ${48 * zoom}px`,
            backgroundPosition: `${pan.x}px ${pan.y}px`,
          }}
        />
        
        {/* Zoom/Pan Transform Container */}
        <div
          className="absolute origin-top-left pointer-events-none"
          style={{
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
            width: canvasBounds.width,
            height: canvasBounds.height,
          }}
        >

        {/* SVG Connections */}
        <svg 
          className="absolute inset-0" 
          style={{ zIndex: 1, overflow: 'visible', pointerEvents: 'none' }}
          width={canvasBounds.width}
          height={canvasBounds.height}
        >
          {connections.map((conn) => {
            const fromEl = elements.find((e) => e.id === conn.from);
            const toEl = elements.find((e) => e.id === conn.to);
            if (!fromEl || !toEl) return null;
            
            // Apply drag delta if element is being dragged
            const from = activeDrag?.id === fromEl.id 
              ? { ...fromEl, x: fromEl.x + activeDrag.delta.x, y: fromEl.y + activeDrag.delta.y }
              : fromEl;
            const to = activeDrag?.id === toEl.id
              ? { ...toEl, x: toEl.x + activeDrag.delta.x, y: toEl.y + activeDrag.delta.y }
              : toEl;
            
            // Calculate best edge points
            const fromPoint = getEdgePoint(from, { x: to.x + to.width / 2, y: to.y + to.height / 2 });
            const toPoint = getEdgePoint(to, fromPoint);
            const path = calculateCurvedPath(fromPoint, toPoint);
            
            return (
              <path
                key={conn.id}
                d={path}
                fill="none"
                stroke="#ff6b00"
                strokeWidth="4"
              />
            );
          })}

          {/* Preview line when connecting */}
          {isDraggingConnection && connectionFromId && (
            (() => {
              const fromEl = elements.find((e) => e.id === connectionFromId);
              if (!fromEl) return null;
              
              const fromPoint = getEdgePoint(fromEl, mousePos);
              const path = calculateCurvedPath(fromPoint, mousePos);
              
              return (
                <path
                  d={path}
                  fill="none"
                  stroke="#ff6b00"
                  strokeWidth="2"
                  strokeDasharray="5,5"
                />
              );
            })()
          )}
        </svg>

        {/* Elements - pointer-events-none on container so clicks pass through to canvas */}
        <div className="relative w-full h-full pointer-events-none" style={{ zIndex: 2 }}>
          {elements.map((el) => (
            <DraggableElement
              key={el.id}
              element={el}
              isSelected={el.id === selectedElementId}
              isConnecting={isDraggingConnection && connectionFromId === el.id}
              isDraggingAnyConnection={isDraggingConnection}
              isDraggingAnyElement={activeDrag !== null}
              onClick={() => handleElementClick(el.id)}
              onConnectDragStart={(e) => handleConnectDragStart(el.id, e)}
              hideControls={hideControls}
              onResize={(handle, mouseX, mouseY, initialEl) => handleResize(el.id, handle, mouseX, mouseY, initialEl)}
              onUpdate={(changes) => updateElement(el.id, changes)}
              scale={totalScale}
            />
          ))}
        </div>

        {/* Empty canvas - show element library */}
        {elements.length === 0 && !hideControls && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-auto" style={{ zIndex: 5 }}>
            <div className="bg-gray-800/95 rounded-2xl p-8 border border-gray-600 shadow-2xl">
              <h3 className="text-white text-2xl font-semibold mb-6 text-center">
                Bibliothèque d'éléments
              </h3>
              <div className="grid grid-cols-2 gap-4">
                {elementTypes.map(({ type, label, icon }) => (
                  <button
                    key={type}
                    onClick={() => handleEmptyCanvasAdd(type)}
                    className="flex flex-col items-center justify-center p-6 bg-blue-600 hover:bg-blue-500 active:bg-blue-700 border-2 border-blue-400 rounded-xl transform hover:scale-105 active:scale-95 shadow-lg"
                  >
                    <span className="text-4xl mb-3">{icon}</span>
                    <span className="text-white font-medium text-lg">{label}</span>
                  </button>
                ))}
              </div>
              <p className="text-gray-400 text-sm mt-6 text-center">
                Cliquez pour ajouter au canvas
              </p>
            </div>
          </div>
        )}

        {/* Void drop picker - show when connection dropped on empty space */}
        {voidDropPicker && !hideControls && (
          <div 
            className="absolute bg-gray-800/95 rounded-xl p-4 border border-accent shadow-2xl"
            style={{ 
              left: voidDropPicker.x, 
              top: voidDropPicker.y,
              transform: 'translate(-50%, -50%)',
              zIndex: 20,
              pointerEvents: 'auto'
            }}
          >
            <p className="text-white text-sm mb-3 text-center font-medium">Créer un élément</p>
            <div className="flex gap-2">
              {elementTypes.map(({ type, label, icon }) => (
                <button
                  key={type}
                  onClick={() => handleVoidDropCreate(type)}
                  className="flex flex-col items-center justify-center p-3 bg-blue-600 hover:bg-blue-500 active:bg-blue-700 border border-blue-400 rounded-lg"
                  title={label}
                >
                  <span className="text-2xl">{icon}</span>
                </button>
              ))}
            </div>
            <button
              onClick={() => setVoidDropPicker(null)}
              className="mt-3 w-full text-gray-400 hover:text-white text-xs"
            >
              Annuler
            </button>
          </div>
        )}

        {/* Connection Delete Buttons Layer - Above everything */}
        {!hideControls && (
          <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 10 }}>
            {connections.map((conn) => {
              const fromEl = elements.find((e) => e.id === conn.from);
              const toEl = elements.find((e) => e.id === conn.to);
              if (!fromEl || !toEl) return null;
              
              // Apply drag delta if element is being dragged
              const from = activeDrag?.id === fromEl.id 
                ? { ...fromEl, x: fromEl.x + activeDrag.delta.x, y: fromEl.y + activeDrag.delta.y }
                : fromEl;
              const to = activeDrag?.id === toEl.id
                ? { ...toEl, x: toEl.x + activeDrag.delta.x, y: toEl.y + activeDrag.delta.y }
                : toEl;
              
              const fromPoint = getEdgePoint(from, { x: to.x + to.width / 2, y: to.y + to.height / 2 });
              const toPoint = getEdgePoint(to, fromPoint);
              const midX = (fromPoint.x + toPoint.x) / 2;
              const midY = (fromPoint.y + toPoint.y) / 2;
              
              return (
                <button
                  key={`delete-${conn.id}`}
                  className="absolute w-6 h-6 bg-red-500 rounded-full border-2 border-white text-white flex items-center justify-center hover:bg-red-600 hover:scale-110"
                  style={{
                    left: midX - 12,
                    top: midY - 12,
                    pointerEvents: 'auto',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    zIndex: 11,
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    console.log('Delete button clicked:', conn.id);
                    removeConnection(conn.id);
                  }}
                  onMouseEnter={() => console.log('Hover delete:', conn.id)}
                  title="Delete connection"
                >
                  ×
                </button>
              );
            })}
          </div>
        )}
        </div>  {/* Close transform container */}
        
        {/* Zoom indicator */}
        {!hideControls && zoom !== 1 && (
          <div className="absolute bottom-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
            {Math.round(zoom * 100)}%
          </div>
        )}
        </div>
      </div>
    </DndContext>
  );
};
